import React, { useState } from 'react';
import { IntlProvider } from 'react-intl';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import AppShell from './components/App/AppShell';
import LegalPage from './components/Legal/LegalPage';
import ErrorBoundary from './components/common/ErrorBoundary';
import { ToastProvider } from './components/common/Toast';
import { defaultLocale, isLocale, locales, Locale } from './i18n';
import { markSignInLocale, peekSignInLocale, takeLocaleFromSearch } from './utils/localePreference';
import './styles/globals.scss';
import './styles/app.scss';

function readInitialLocale(): Locale {
  const fromQuery = takeLocaleFromSearch(window.location.search);
  if (fromQuery) {
    markSignInLocale(fromQuery);
    const params = new URLSearchParams(window.location.search);
    params.delete('lang');
    const search = params.toString();
    const next = `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`;
    window.history.replaceState({}, '', next);
    return fromQuery;
  }

  const pending = peekSignInLocale();
  if (pending) return pending;

  const stored = localStorage.getItem('locale');
  return isLocale(stored) ? stored : defaultLocale;
}

const App: React.FC = () => {
  const [locale, setLocale] = useState<Locale>(readInitialLocale);

  const handleLocaleChange = (l: Locale) => {
    setLocale(l);
    localStorage.setItem('locale', l);
  };

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <IntlProvider locale={locale} messages={locales[locale]}>
          <ToastProvider>
            <AuthProvider>
              <Routes>
                <Route path="/privacy" element={<LegalPage doc="privacy" />} />
                <Route path="/terms" element={<LegalPage doc="terms" />} />
                <Route path="/*" element={<AppShell locale={locale} onLocaleChange={handleLocaleChange} />} />
              </Routes>
            </AuthProvider>
          </ToastProvider>
        </IntlProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
};

export default App;
